import React, {createContext, useCallback, useContext, useRef, useState} from 'react';
import { OI } from '../../onam/mark';
import { FEATURES } from '../../onam/features';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType, durationMs?: number) => void;
  dismissAll: () => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

// ONAM skin (FEATURES.curationSkin): the brand's own status colours and glyphs instead of the stock set
const SKIN: Record<ToastType, {bg: string; border: string; glyph: string}> = {
  success: {bg: '#FFFFFF', border: '#2E7D5B', glyph: 'check'},
  error:   {bg: '#FFFFFF', border: '#A63D3D', glyph: 'close'},
  info:    {bg: '#FFFFFF', border: '#3E7BB8', glyph: 'info'},
  warning: {bg: '#FFFFFF', border: '#C77D2E', glyph: 'warn'},
};

const COLORS: Record<ToastType, {bg: string; border: string; icon: string}> = {
  success: {bg: '#f0fdf4', border: '#22c55e', icon: '✓'},
  error:   {bg: '#fff1f0', border: '#FF5349', icon: '✕'},
  info:    {bg: '#eff6ff', border: '#3b82f6', icon: 'ℹ'},
  warning: {bg: '#fffbeb', border: '#f59e0b', icon: '⚠'},
};

const AUTO_DISMISS_MS = 4000;

export function ToastProvider({children}: {children: React.ReactNode}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const showToast = useCallback((message: string, type: ToastType = 'success', durationMs: number = AUTO_DISMISS_MS) => {
    const id = `${Date.now()}-${Math.random()}`;
    setToasts((prev) => [...prev, {id, message, type}]);
    const timer = setTimeout(() => dismiss(id), durationMs);
    timers.current.set(id, timer);
  }, [dismiss]);

  const dismissAll = useCallback(() => {
    timers.current.forEach((timer) => clearTimeout(timer));
    timers.current.clear();
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{showToast, dismissAll}}>
      {children}
      <div
        role="region"
        aria-label="Notifications"
        aria-live="polite"
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 'var(--z-toast, 2000)' as any,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => {
          const skin = FEATURES.curationSkin ? SKIN[toast.type] : null;
          const {bg, border} = skin || COLORS[toast.type];
          const icon = skin ? <OI n={skin.glyph} size={16} /> : COLORS[toast.type].icon;
          return (
            <div
              key={toast.id}
              role="alert"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                background: bg,
                borderLeft: `4px solid ${border}`,
                borderRadius: 'var(--radius-sm)',
                boxShadow: 'var(--shadow-md)',
                padding: '12px 16px',
                minWidth: 260,
                maxWidth: 400,
                fontFamily: skin ? 'var(--sans)' : 'Roboto, sans-serif',
                fontSize: 14,
                color: skin ? '#123458' : '#23183f',
                pointerEvents: 'all',
                animation: 'toastSlideIn 0.2s ease',
              }}
            >
              <span style={{fontWeight: 700, color: border, fontSize: 16, lineHeight: 1, display: 'inline-flex'}}>
                {icon}
              </span>
              <span style={{flex: 1}}>{toast.message}</span>
              <button
                onClick={() => dismiss(toast.id)}
                aria-label="Dismiss notification"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#797979',
                  fontSize: 16,
                  lineHeight: 1,
                  padding: '0 0 0 4px',
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes toastSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
